import { useEffect, useMemo, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus } from "lucide-react";
import { usePaymentDeskRefData } from "../shared/usePaymentDeskRefData";
import ImprestLogAdvanceFlow from "./ImprestLogAdvanceFlow";
import ImprestBillFormDialog from "./ImprestBillFormDialog";
import ImprestSettleDialog from "./ImprestSettleDialog";
import ImprestEmployeeView from "./ImprestEmployeeView";
import { BalanceLabel, StatusBadge, money } from "./imprestShared";

// Payment Desk > Imprest: employees with imprest activity and their running balance.
export default function ImprestTab() {
  const refData = usePaymentDeskRefData();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [advanceFor, setAdvanceFor] = useState(null);
  const [billFor, setBillFor] = useState(null);
  const [settle, setSettle] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = () => {
    setLoading(true);
    client.get("/payment-desk/imprest/employees").then((res) => setRows(res.data)).catch((err) => toast.error(getErrorMessage(err))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const refresh = () => { load(); setReloadKey((k) => k + 1); };

  const billEmployees = useMemo(() => {
    const map = new Map((refData.payeeEmployees || []).map((e) => [e.employee_id, e]));
    rows.forEach((r) => { if (!map.has(r.employee_id)) map.set(r.employee_id, { employee_id: r.employee_id, name: r.employee_name }); });
    return [...map.values()];
  }, [refData.payeeEmployees, rows]);

  return (
    <div className="space-y-4" data-testid="imprest-tab">
      {selected ? (
        <ImprestEmployeeView
          employeeId={selected} reloadKey={reloadKey} onBack={() => setSelected(null)} onChanged={load}
          onLogAdvance={(id) => setAdvanceFor(id)} onAddBill={(id) => setBillFor(id)} onSettle={setSettle}
        />
      ) : (
        <>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setBillFor("")} data-testid="imprest-add-bill-button"><Plus className="h-4 w-4 mr-1" /> Add Bill</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => setAdvanceFor("")} data-testid="imprest-log-advance-button"><Plus className="h-4 w-4 mr-1" /> Log Advance</Button>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <Table data-testid="imprest-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Total Advances Given</TableHead>
                  <TableHead className="text-right">Total Bills Tallied</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400" data-testid="imprest-empty">No imprest activity yet. Log an advance to begin.</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.employee_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(r.employee_id)} data-testid={`imprest-row-${r.employee_id}`}>
                    <TableCell className="font-medium">{r.employee_name} <span className="text-xs text-slate-400">({r.employee_id})</span></TableCell>
                    <TableCell className="text-right" data-testid={`imprest-row-advances-${r.employee_id}`}>{money(r.total_advances)}</TableCell>
                    <TableCell className="text-right" data-testid={`imprest-row-bills-${r.employee_id}`}>{money(r.total_bills)}</TableCell>
                    <TableCell><BalanceLabel balance={r.balance} testId={`imprest-row-balance-${r.employee_id}`} /></TableCell>
                    <TableCell><StatusBadge status={r.status} testId={`imprest-row-status-${r.employee_id}`} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {advanceFor !== null && (
        <ImprestLogAdvanceFlow refData={refData} employeeId={advanceFor} onClose={() => setAdvanceFor(null)} onSaved={() => { setAdvanceFor(null); refresh(); }} />
      )}
      {billFor !== null && (
        <ImprestBillFormDialog refData={refData} employees={billEmployees} employeeId={billFor} onClose={() => setBillFor(null)} onSaved={() => { setBillFor(null); refresh(); }} />
      )}
      {settle && (
        <ImprestSettleDialog {...settle} onClose={() => setSettle(null)} onSettled={() => { setSettle(null); refresh(); }} />
      )}
    </div>
  );
}
